/**
 * Contraste: tornar legível uma cor que outra pessoa escolheu.
 *
 * O produto deixa a clínica escolher a cor do convênio, da etiqueta, do canal.
 * A etiqueta usa essa cor duas vezes — tingida no fundo e cheia no texto — e é
 * aí que ela quebra: `#22C55E` sobre o próprio tom a 12% dá **2,05:1**, quando
 * a norma pede 4,5:1. Não é um verde mal escolhido; é o padrão que não fecha
 * para nenhuma cor de luminância média, e verde, âmbar e violeta são
 * exatamente isso.
 *
 * A saída não é uma lista de cores aprovadas — a clínica vai escolher a dela, e
 * uma lista não a alcança. É **escurecer a cor do texto até ela passar**,
 * mantendo o matiz. O convênio continua sendo verde; o verde do texto é que
 * fica escuro o bastante para ser lido. O ponto colorido e o fundo tingido
 * seguem com a cor original, e é neles que a identidade se vê.
 */

/** Luminância relativa da WCAG. */
function luminancia(rgb: [number, number, number]): number {
    const [r, g, b] = rgb.map((v) => {
        const c = v / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    }) as [number, number, number];
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function paraRgb(hex: string): [number, number, number] | null {
    const limpo = hex.trim().replace('#', '');
    const cheio = limpo.length === 3 ? limpo.split('').map((c) => c + c).join('') : limpo;
    if (!/^[0-9a-fA-F]{6}$/.test(cheio)) return null;
    return [
        parseInt(cheio.slice(0, 2), 16),
        parseInt(cheio.slice(2, 4), 16),
        parseInt(cheio.slice(4, 6), 16),
    ];
}

function paraHex(rgb: [number, number, number]): string {
    return `#${rgb.map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0')).join('')}`;
}

/** A razão de contraste entre duas cores, como a WCAG a define. */
export function razaoDeContraste(a: string, b: string): number {
    const ra = paraRgb(a);
    const rb = paraRgb(b);
    if (!ra || !rb) return 1;
    const la = luminancia(ra);
    const lb = luminancia(rb);
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * Mistura a cor com o branco, na proporção que a etiqueta usa no fundo.
 *
 * O fundo da etiqueta é a cor a 12% (`1F` em hexadecimal) sobre a superfície.
 * O navegador compõe isso; para medir o contraste é preciso compor também —
 * medir contra a cor cheia diria que a etiqueta é ilegível quando ela não é.
 */
export function fundoTingido(cor: string, alfa = 0.12, superficie = '#FFFFFF'): string {
    const c = paraRgb(cor);
    const s = paraRgb(superficie);
    if (!c || !s) return superficie;
    return paraHex([0, 1, 2].map((i) => c[i]! * alfa + s[i]! * (1 - alfa)) as [number, number, number]);
}

const ALVO_AA = 4.5;

/**
 * A mesma cor, escura o bastante para ser lida sobre `fundo`.
 *
 * Escurece por passos pequenos e para no primeiro que passa: parar no primeiro
 * mantém a cor o mais próxima possível da que a clínica escolheu, e é isso que
 * faz a etiqueta continuar parecendo dela. Cor inválida volta como veio — a
 * tela não é lugar de validar entrada, e um `#zzz` não pode apagar o texto.
 */
export function corLegivel(cor: string, fundo: string, alvo = ALVO_AA): string {
    const rgb = paraRgb(cor);
    if (!rgb) return cor;
    if (razaoDeContraste(cor, fundo) >= alvo) return cor;

    /* 40 passos de 2,5% cobrem do tom original até o quase-preto. Se nem o
       quase-preto passar, o fundo é que é escuro demais, e devolver preto é a
       melhor resposta possível. */
    let atual = rgb;
    for (let i = 0; i < 40; i += 1) {
        atual = atual.map((v) => v * 0.975) as [number, number, number];
        const hex = paraHex(atual);
        if (razaoDeContraste(hex, fundo) >= alvo) return hex;
    }
    return '#000000';
}

/**
 * O par pronto para uma etiqueta tingida: fundo e texto.
 *
 * Um lugar só. Três telas repetiam `background: cor+1F; color: cor`, e a
 * terceira teria repetido o defeito de novo.
 */
export function etiquetaTingida(cor: string, superficie = '#FFFFFF'): { fundo: string; texto: string } {
    const fundo = fundoTingido(cor, 0.12, superficie);
    return { fundo, texto: corLegivel(cor, fundo) };
}

/** O mesmo par, com os nomes que o CSS espera — para espalhar no `style`. */
export function estiloDaEtiqueta(cor: string, superficie = '#FFFFFF'): { background: string; color: string } {
    const { fundo, texto } = etiquetaTingida(cor, superficie);
    return { background: fundo, color: texto };
}
